import React, { useState, useContext, useEffect } from "react";
import axios from "axios";
import {
  Container,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
} from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../../components/Context/AuthContext";
import API_URL from "../../config";
import { useLoading } from "../../components/LoadingContext/LoadingContext";
import API_BASE_URL from "../../utils/api";

const Login = () => {
  useEffect(() => {
    document.title = "SignIn - MS Digital Hub";
  }, []);
  const { setUser } = useContext(AuthContext);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [signInCode, setSignInCode] = useState("");
  const [error, setError] = useState("");
  const [useCode, setUseCode] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [codeExpires, setCodeExpires] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/dh-hub";
  const logoutMessage = location.state?.message || "";
  const fromPath = location.state?.from?.pathname || "";
  const isTranscriptionAttempt =
    fromPath === "/transcription" ||
    fromPath.startsWith("/transcription/") ||
    fromPath.startsWith("/document/");

  const { setLoading, setLoadingMessage } = useLoading();

  const handleLogin = async (e) => {
    e.preventDefault();
    localStorage.removeItem("token");
    setLoadingMessage(
      "Verifying your credentials.... This may take a few moments."
    );
    setLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      if (res.data.token) {
        const { token, user } = res.data;
        localStorage.setItem("token", token);
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      } else {
        console.error("token is missing in response");
      }
      setUser(res.data.user);
      navigate(from, { replace: true });
    } catch (err) {
      const errorMessage =
        err.response?.data?.error || "Login failed. Please try again.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSendSignInCode = async () => {
    setError("");
    setLoadingMessage("Sending sign-in code...");
    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/auth/send-signin-code`, {
        email,
      });
      setCodeSent(true);
      setUseCode(true);
      setCodeExpires("10 minutes");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to send sign-in code.");
    } finally {
      setLoading(false);
    }
  };
  const handleSignInWithCode = async () => {
    setError("");
    setLoadingMessage("Verifying sign-in code....");
    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/auth/verify-sign-in-code`, {
        email,
        signInCode,
      });
      localStorage.setItem("token", res.data.token);
      axios.defaults.headers.common["Authorization"] =
        `Bearer ${res.data.token}`;
      setUser(res.data.user);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || "Invalid sign-in code.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xs">
      <Card sx={{ mt: 8, boxShadow: 3 }}>
        <CardContent>
          <Typography variant="h4" component="h2" align="center" gutterBottom>
            Login
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {logoutMessage && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {logoutMessage}
            </Alert>
          )}
          {isTranscriptionAttempt && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                borderRadius: 2,
                backgroundColor: "#f5f9ff",
                border: "1px solid #d0e2ff",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", lineHeight: 1.6 }}
              >
                You're trying to access the <b> Transcription Tool</b>. Please
                sign in or create an account to begin volunteering.
              </Typography>
            </Box>
          )}
          <Box
            component="form"
            onSubmit={handleLogin}
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            <TextField
              label="Email"
              type="email"
              variant="outlined"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              fullWidth
              sx={{ mb: 2 }}
              disabled={codeSent}
            />
            {!useCode && (
              <>
                <TextField
                  label="Password"
                  type="password"
                  variant="outlined"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  fullWidth
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  sx={{ mt: 1 }}
                  disabled={!(email.length > 0 && password.length > 0)}
                >
                  Login
                </Button>

                <Typography color="textSecondary" sx={{ textAlign: "center" }}>
                  OR
                </Typography>
              </>
            )}
            {useCode && (
              <>
                {codeSent ? (
                  <>
                    <Typography color="textSecondary" sx={{ mb: 1 }}>
                      Code expires in {codeExpires}
                    </Typography>
                    <TextField
                      label="Sign-in code"
                      type="text"
                      variant="outlined"
                      value={signInCode}
                      onChange={(e) => setSignInCode(e.target.value)}
                      required
                      fullWidth
                      sx={{ mb: 2 }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      sx={{ mb: 2 }}
                      onClick={handleSignInWithCode}
                      disabled={!(signInCode.length > 0)}
                    >
                      Sign in with Code
                    </Button>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ textAlign: "center" }}
                    >
                      Didn't receive a code?
                      <Button
                        variant="text"
                        color="primary"
                        sx={{ textTransform: "none" }}
                        onClick={handleSendSignInCode}
                      >
                        Re-send Code
                      </Button>
                    </Typography>
                  </>
                ) : (
                  <>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSendSignInCode}
                      disabled={!(email.length > 0)}
                    >
                      Send Code
                    </Button>
                    <Typography
                      color="textSecondary"
                      sx={{ textAlign: "center" }}
                    >
                      OR
                    </Typography>
                  </>
                )}
              </>
            )}

            {!codeSent && (
              <Button
                variant="outlined"
                fullWidth
                sx={{ mt: 0 }}
                onClick={() => setUseCode(!useCode)}
              >
                {useCode ? "Use Password" : "Use Sign-in code"}
              </Button>
            )}
            <Button
              variant="text"
              fullWidth
              sx={{
                mt: 0,
                textDecoration: "underline",
                "&:hover": { textDecoration: "underline" },
              }}
              onClick={() => navigate("/forgot-password")}
            >
              {" "}
              Forgot Password?
            </Button>

            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ textAlign: "center" }}
            >
              New to Hub?
              <Button
                variant="text"
                color="primary"
                sx={{ textTransform: "none" }}
                onClick={() => navigate("/signup")}
              >
                Sign up now
              </Button>
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default Login;
